import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import * as PageBusinessCardActions from '@planone/bau365/data-access-business-card';
import { RedirectUrlGenerator } from '@planone/bau365/util-keycloak';
import { NotificationService } from '@planone/bau365/util-notification';
import { UserDataService } from '@planone/bau365/util-user-data';
import * as DataAccessCountriesActions from '@planone/shared/data-access-countries';
import { WindowRef } from '@planone/shared/util-browser-api';
import { DownloadFileGenerator } from '@planone/shared/util-downloads';
import { SnackBarType } from '@planone/shared/util-interfaces';
import { KeycloakEvent, KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, catchError, defer, map, mapTo, of, switchMap, withLatestFrom } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import * as DataAccessUserActions from './data-access-user.actions';
import { sendBusinessCardButtonActions } from './data-access-user.actions';
import { userIsLoggedIn } from './data-access-user.actions';
import * as DataAccessUserSelectors from './data-access-user.selectors';

import { USER_SESSION_STATUS } from '../../config/user-service.config.interface';
import { UserService } from '../../service/user/user.service';

@Injectable()
export class DataAccessUserEffects {
  private redirectTimeout?: ReturnType<typeof setTimeout>;

  loadSynonyms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfileCompletionPageOpened, DataAccessUserActions.userProfilePageOpened),
      switchMap(() =>
        this.userService.getAvailableSynonyms().pipe(
          map((synonyms) => DataAccessUserActions.synonymsLoadSucceeded({ synonyms })),
          catchError((err) => of(DataAccessUserActions.synonymsLoadFailed({ error: err.status.toString() })))
        )
      )
    )
  );

  submitProfessionSpecialization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.professionSpecializationSubmitted),
      switchMap(({ profession, specialization, synonym }) =>
        this.userService.setProfessionSpecialization(profession, specialization, synonym).pipe(
          map((result) =>
            DataAccessUserActions.professionSpecializationsSubmitSucceeded({
              profession: result.professions[0],
              specialization: result.specializations[0],
              synonym: result.synonyms ? result.synonyms[0] : undefined,
            })
          ),
          catchError((err) =>
            of(DataAccessUserActions.professionSpecializationsSubmitFailed({ error: err.status.toString() }))
          )
        )
      )
    )
  );

  submitProfessionSpecializationSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.professionSpecializationsSubmitSucceeded),
        withLatestFrom(this.store.pipe(select(DataAccessUserSelectors.getUserProfileCompletionRedirectUrl))),
        map(([, redirectUrl]) => (redirectUrl ? this.router.parseUrl(decodeURIComponent(redirectUrl)) : null)),
        withLatestFrom(this.route.queryParamMap),
        map(([redirectUrl, queryParams]) => {
          if (redirectUrl === null) return null;
          const sendBusinessCard = redirectUrl.queryParamMap.get('sendBusinessCard');
          const fairId = queryParams.get('fairId');
          const exhibitorId = queryParams.get('exhibitorId');
          if (sendBusinessCard !== 'true' || !fairId || !exhibitorId) return redirectUrl;
          return this.urlGenerator.createInternalUrl(['/', 'profile', 'business-card'], {
            queryParams: { sendBusinessCard, fairId, exhibitorId },
          });
        }),
        tap((redirectUrl) =>
          redirectUrl !== null
            ? this.router.navigateByUrl(redirectUrl)
            : this.router.navigate(['/'], { queryParams: { registrationCompleted: true } })
        )
      ),
    { dispatch: false }
  );

  loadUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfilePageOpened),
      withLatestFrom(this.store.pipe(select(DataAccessUserSelectors.getUserProfile))),
      filter(([_action, userProfile]) => userProfile.title == '' || userProfile.shortDescription == ''),
      switchMap(() => {
        return this.userService.getUserProfile().pipe(
          map((result) =>
            DataAccessUserActions.userProfileLoadedSucceeded({
              title: result.title,
              shortDescription: result.description,
            })
          ),
          catchError((err) => {
            return of(DataAccessUserActions.userProfileLoadedFailed({ error: err.status.toString() }));
          })
        );
      })
    )
  );

  loadUserProfessionalInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfilePageOpened, DataAccessUserActions.userProfileCompletionPageOpened),
      switchMap(() =>
        this.userService.getUserProfessionalInformation().pipe(
          map((result) => DataAccessUserActions.userProfessionalInformationLoadSucceeded(result)),
          catchError((err) =>
            of(DataAccessUserActions.userProfessionalInformationLoadFailed({ error: err.status.toString() }))
          )
        )
      )
    )
  );

  submitKeycloakData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.profileSubmitted),
      switchMap((action) => {
        return this.userService.setKeycloakData(action.givenName, action.familyName).pipe(
          map(() =>
            DataAccessUserActions.keycloakProfileSubmitSucceeded({
              givenName: action.givenName,
              familyName: action.familyName,
            })
          ),
          catchError((err) => of(DataAccessUserActions.keycloakProfileSubmitFailed({ error: err.status.toString() })))
        );
      })
    )
  );

  submitKeycloakDataSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.keycloakProfileSubmitSucceeded),
        tap((_action) => {
          this.notificationService.projectContent(
            $localize`:@@userEffect-keycloakProfileSubmitSucceeded:Speichern erfolgreich`,
            SnackBarType.success,
            undefined,
            true
          );
        })
      ),
    { dispatch: false }
  );

  submitKeycloakDataFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.keycloakProfileSubmitFailed),
        tap((_action) => {
          this.notificationService.projectContent(
            $localize`:@@userEffect-keycloakProfileSubmitFailed:Speichern nicht erfolgreich`,
            SnackBarType.failure,
            undefined,
            true
          );
        })
      ),
    { dispatch: false }
  );

  loadUserJobInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        userIsLoggedIn,
        DataAccessUserActions.userProfileImageSubmitSucceeded,
        DataAccessUserActions.userProfileImageDeleteSucceeded
      ),
      switchMap(() => {
        return this.userService.getUserJobInformation().pipe(
          map((result) => DataAccessUserActions.userJobInformationLoadSucceeded({ jobInformation: result })),
          catchError((err) => {
            return of(DataAccessUserActions.userJobInformationLoadFailed({ error: err.status.toString() }));
          })
        );
      })
    )
  );

  submitUserJobInformationToUserService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userJobInformationSubmitted),
      switchMap((action) => {
        return this.userService.setJobInformation(action.jobInformation).pipe(
          map((result) =>
            DataAccessUserActions.userJobInformationSubmitSucceeded({
              jobInformation: result,
            })
          ),
          catchError((err) =>
            of(DataAccessUserActions.userJobInformationSubmitFailed({ error: err.status.toString() }))
          )
        );
      })
    )
  );

  userJobInformationSubmitSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.userJobInformationSubmitSucceeded),
        tap(() =>
          this.notificationService.projectContent(
            $localize`:@@featureUserProfile-userJobInformationSubmitSucceeded:Die Daten Ihrer Visitenkarte wurden erfolgreich angepasst.`,
            SnackBarType.success
          )
        )
      ),
    { dispatch: false }
  );

  userJobInformationSubmitFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.userJobInformationSubmitFailed),
        tap(() =>
          this.notificationService.projectContent(
            $localize`:@@featureUserProfile-userJobInformationSubmitFailed:Ein Fehler ist aufgetreten, bitte versuchen Sie es später erneut.`,
            SnackBarType.failure
          )
        )
      ),
    { dispatch: false }
  );

  loadEmployeeCountOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfilePageOpened),
      switchMap(() => {
        return this.userDataService.getEmployeeCountOptions().pipe(
          map((result) => DataAccessUserActions.loadEmployeeCountOptionsSuccess({ employeeCountOptions: result })),
          catchError((err) => {
            return of(DataAccessUserActions.loadEmployeeCountOptionsFailure({ error: err.status.toString() }));
          })
        );
      })
    )
  );

  loadAreaOfInterestOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfilePageOpened),
      switchMap(() => {
        return this.userDataService.getAreaOfInterestOptions().pipe(
          map((result) => DataAccessUserActions.loadAreaOfInterestOptionsSuccess({ areaOfInterestOptions: result })),
          catchError((err) => {
            return of(DataAccessUserActions.loadAreaOfInterestOptionsFailure({ error: err.status.toString() }));
          })
        );
      })
    )
  );

  userIsLoggedIn$ = createEffect(() =>
    defer(() => {
      return this.keycloakService.keycloakEvents$.pipe(
        filter((event) => event.type === KeycloakEventType.OnAuthSuccess),
        mapTo(userIsLoggedIn())
      );
    })
  );

  loadUserProfileOnInit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(userIsLoggedIn),
      switchMap(() => {
        return this.userService.getUserProfessionalInformation().pipe(
          map((response) =>
            DataAccessUserActions.savedProfessionSpecializationLoaded({
              profession: response?.professions[0],
              specialization: response?.specializations[0],
              synonyms: response?.synonyms?.[0],
            })
          ),
          catchError(() => of(DataAccessUserActions.savedProfessionSpecializationNotFound()))
        );
      })
    );
  });

  loadUserRolesOnInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userIsLoggedIn),
      map(() => {
        const userRoles = this.keycloakService.getUserRoles();
        return DataAccessUserActions.userRolesLoaded({ userRoles: userRoles });
      })
    )
  );

  submitNewsletterSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.newsletterSubscriptionSubmitted),
      switchMap((action) => {
        return this.userService.requestNewsletterSignup(action.email).pipe(
          map(() =>
            DataAccessUserActions.newsletterSubscriptionSucceeded({
              email: action.email,
            })
          ),
          catchError((_err, _caught) => of(DataAccessUserActions.newsletterSubscriptionFailed()))
        );
      })
    )
  );

  newsletterSubscriptionSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.newsletterSubscriptionSucceeded),
        tap((_action) => {
          this.notificationService.projectContent(
            'Anmeldung erfolgreich. Bitte bestätige deine E-Mail Adresse.',
            SnackBarType.success,
            'reload'
          );
        })
      ),
    { dispatch: false }
  );

  newsletterSubscriptionFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.newsletterSubscriptionFailed),
        tap((_action) => {
          this.notificationService.projectContent(
            'Anmeldung fehlgeschlagen. Bitte überprüfe deine E-Mail Adresse und versuche es erneut.',
            SnackBarType.failure,
            'reload'
          );
        })
      ),
    { dispatch: false }
  );

  sessionState$ = createEffect(
    () =>
      this.keycloakService.keycloakEvents$.pipe(
        map((keycloakEvent: KeycloakEvent) => {
          if (isPlatformServer(this.platform)) {
            this.userSessionStatus.next(false);

            return;
          }

          if (KeycloakEventType.OnReady === keycloakEvent.type) {
            this.userSessionStatus.next(this.keycloakService.isLoggedIn());
          } else if (
            KeycloakEventType.OnAuthRefreshError === keycloakEvent.type ||
            KeycloakEventType.OnAuthLogout === keycloakEvent.type
          ) {
            this.userSessionStatus.next(false);
          } else if (
            KeycloakEventType.OnAuthSuccess === keycloakEvent.type ||
            KeycloakEventType.OnAuthRefreshSuccess === keycloakEvent.type
          ) {
            this.userSessionStatus.next(true);
          }
        })
      ),
    { dispatch: false }
  );

  submitDeleteAccountToUserService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.deleteAccountSubmitted),
      switchMap((action) => {
        return this.userService.deleteAccount(action.predefinedReason, action.otherReason).pipe(
          map(() => DataAccessUserActions.deleteAccountSubmitSucceeded()),
          catchError((err) => of(DataAccessUserActions.deleteAccountSubmitFailed({ error: err.status.toString() })))
        );
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.deleteAccountSubmitSucceeded),
        tap((_action) => {
          this.notificationService.projectContent(
            $localize`:@@featureUserProfile-logoutAfterCancellation:Deine Kündigung ist bei uns eingegangen. Bitte überprüfe deinen E-Maileingang. Du wirst nun abgemeldet.`,
            SnackBarType.success
          );
          this.redirectTimeout = setTimeout(() => {
            this.keycloakService.getKeycloakInstance().logout({
              redirectUri: this.windowRef.nativeWindow.location.href.replace(this.router.url, '') + '/logout',
            });
          }, 2000);
        })
      ),
    { dispatch: false }
  );

  jumpToBusinessCard = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.showBusinessCard, DataAccessUserActions.createBusinessCardSuccess),
        withLatestFrom(this.route.queryParamMap),
        tap(([_, params]) => {
          const sendBusinessCard = params.get('sendBusinessCard');
          if (sendBusinessCard === 'true') {
            const fairId = params.get('fairId');
            const exhibitorId = params.get('exhibitorId');
            if (fairId && exhibitorId) {
              this.router.navigate(['/', 'fairs', fairId, 'exhibitors', exhibitorId], {
                queryParams: { sendBusinessCard, origin: 'createBusinessCard' },
              });
              return;
            }
          }
          this.router.navigate(['profile', 'business-card']);
        })
      ),
    { dispatch: false }
  );

  initBusinessCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DataAccessUserActions.userProfilePageOpened,
        DataAccessUserActions.userBusinessCardPageOpened,
        PageBusinessCardActions.loadBusinessCardSuccess,
        sendBusinessCardButtonActions.initButton
      ),
      map(() => DataAccessUserActions.loadBusinessCard())
    )
  );

  jumpToProfile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.changeBusinessCardDetails, DataAccessUserActions.startBusinessCardCreation),
        tap(() => this.router.navigate(['profile'], { queryParamsHandling: 'merge' }))
      ),
    { dispatch: false }
  );

  loadBusinessCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.loadBusinessCard),
      switchMap(() =>
        this.store.pipe(select(DataAccessUserSelectors.getBusinessCard)).pipe(
          switchMap((businessCard) =>
            businessCard
              ? of(DataAccessUserActions.loadBusinessCardSuccess({ businessCard }))
              : this.userService.getBusinessCard().pipe(
                  map((businessCard) => DataAccessUserActions.loadBusinessCardSuccess({ businessCard })),
                  catchError((error) => of(DataAccessUserActions.loadBusinessCardFailure({ error })))
                )
          )
        )
      )
    )
  );

  createBusinessCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.createBusinessCard),
      switchMap(() =>
        this.userService.createBusinessCard().pipe(
          map((businessCard) => DataAccessUserActions.createBusinessCardSuccess({ businessCard })),
          catchError((error) => of(DataAccessUserActions.createBusinessCardFailure({ error })))
        )
      )
    )
  );

  resetBusinessCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.resetBusinessCard),
      switchMap(() =>
        this.userService.createBusinessCard().pipe(
          map((businessCard) => DataAccessUserActions.resetBusinessCardSuccess({ businessCard })),
          catchError((error) => of(DataAccessUserActions.resetBusinessCardFailure({ error })))
        )
      )
    )
  );

  downloadBusinessCardQrCode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataAccessUserActions.downloadBusinessCardQrCode),
        tap(({ canvas }) =>
          this.downloads.downloadToImage({
            canvas,
            name: $localize`:@@featureUserProfile-downloadQrCode-filename:Digitale Visitenkarte`,
            format: 'png',
          })
        )
      ),
    { dispatch: false }
  );

  initCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfilePageOpened),
      withLatestFrom(of(this.localeId)),
      map(([_, localeId]) => (localeId === 'de' ? 'de' : 'en')),
      map((localeId) => DataAccessCountriesActions.initCountries({ localeId }))
    )
  );

  submitProfileImageToUserService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfileImageSubmitted),
      switchMap(({ profileImage }) =>
        this.userService.setProfileImage(profileImage).pipe(
          map(() => DataAccessUserActions.userProfileImageSubmitSucceeded()),
          catchError((err) => of(DataAccessUserActions.userProfileImageSubmitFailed({ error: err.status.toString() })))
        )
      )
    )
  );

  deleteProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessUserActions.userProfileImageDelete),
      switchMap(() =>
        this.userService.deleteProfileImage().pipe(
          map(() => DataAccessUserActions.userProfileImageDeleteSucceeded()),
          catchError((err) => of(DataAccessUserActions.userProfileImageDeleteFailed({ error: err.status.toString() })))
        )
      )
    )
  );

  sendBusinessCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendBusinessCardButtonActions.sendBusinessCard),
      switchMap(({ fairId, exhibitorId }) =>
        this.userService.sendBusinessCard(fairId, exhibitorId).pipe(
          map(() => sendBusinessCardButtonActions.sendBusinessCardSuccessful({ exhibitorId })),
          catchError((error) => of(sendBusinessCardButtonActions.sendBusinessCardFailed({ error })))
        )
      )
    )
  );

  sendBusinessCardSuccessfulMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendBusinessCardButtonActions.sendBusinessCardSuccessful),
        tap(() => {
          this.notificationService.projectContent(
            $localize`:@@userEffect-sendBusinessCardSuccessful:Ihre Visitenkarte wurde erfolgreich übermittelt`,
            SnackBarType.success
          );
        })
      ),
    { dispatch: false }
  );

  sendBusinessCardRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          sendBusinessCardButtonActions.sendBusinessCardSuccessful,
          sendBusinessCardButtonActions.sendBusinessCardRejected
        ),
        withLatestFrom(this.route.queryParamMap),
        map(([, params]) => params.get('sendBusinessCard')),
        filter((sendBusinessCard) => !!sendBusinessCard),
        tap(() =>
          this.router.navigate([], {
            queryParams: { sendBusinessCard: null, fairId: null, exhibitorId: null, origin: null },
            queryParamsHandling: 'merge',
          })
        )
      ),
    { dispatch: false }
  );

  sendBusinessCardFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sendBusinessCardButtonActions.sendBusinessCardFailed),
        tap(() => {
          this.notificationService.projectContent(
            $localize`:@@userEffect-sendBusinessCardFailed:Da ist etwas schief gelaufen: Ihre Visitenkarte konnte leider nicht übermittelt werden`,
            SnackBarType.failure
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private userService: UserService,
    private keycloakService: KeycloakService,
    private downloads: DownloadFileGenerator,
    private router: Router,
    private route: ActivatedRoute,
    private readonly store: Store,
    private readonly notificationService: NotificationService,
    private windowRef: WindowRef,
    private dialog: MatDialog,
    private urlGenerator: RedirectUrlGenerator,
    @Inject(USER_SESSION_STATUS) private userSessionStatus: BehaviorSubject<boolean>,
    @Inject(PLATFORM_ID) private platform: string,
    @Inject(LOCALE_ID) private localeId: string,
    private userDataService: UserDataService
  ) {}
}
